import { ContentEdit } from '@fs/zion-icon'
import { Button, DialogOverlay, DialogOverlayActions, DialogOverlayFooter, Select, TextField } from '@fs/zion-ui'
import { getUser } from '@fs/zion-user'
import React, { useContext, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { setIdInIdentifiers } from '../../../../util/StringUtil'
import ModifyCtrPersonTable from './ModifyCtrPersonTable'
import { putCtr } from '../../../../services/CtrService'
import useNotifications from '../../../../hooks/useNotifications'
import { CtrContext } from '../../CtrContext'
import { getCisIdAndContributorIdPair } from '../../../../services/userService'

export default function ModifyCtrDialog({ templePlatform, modifiedPersons, ...overlay }) {
  const { t } = useTranslation()
  const { error, ok } = useNotifications()
  const ctrContext = useContext(CtrContext)
  const [reasonType, setReasonType] = useState('OTHER')
  const [reasonText, setReasonText] = useState()
  const [modifyingCtr, setModifyingCtr] = useState(false)
  const focusElementRef = useRef()
  const changeCtrReasonOptions = [
    {
      value: 'BIC_OVERRIDE_BECAUSE_NOT_BIC',
      label: t('ctr.editReasonType.BIC_OVERRIDE_BECAUSE_NOT_BIC', 'Override Because not BIC'),
    },
    {
      value: 'BIC_OVERRIDE_BY_FIRST_PRESIDENCY_AUTHORIZATION',
      label: t(
        'ctr.editReasonType.BIC_OVERRIDE_BY_FIRST_PRESIDENCY_AUTHORIZATION',
        'BIC Override by First Presidency Authorization'
      ),
    },
    {
      value: 'BIC_REVERT_BACK_TO_BIC',
      label: t('ctr.editReasonType.BIC_REVERT_BACK_TO_BIC', 'Revert Back to BIC'),
    },
    {
      value: 'CHANGE_CTR_PERSON_TO_MATCH_OWS_PERSON',
      label: t('ctr.editReasonType.CHANGE_CTR_PERSON_TO_MATCH_OWS_PERSON', 'Change CTR Person to Match OWS Person'),
    },
    {
      value: 'CMIS_TEMPLE_ORDINANCE',
      label: t('ctr.editReasonType.CMIS_TEMPLE_ORDINANCE', 'CMIS Temple Ordinance'),
    },
    { value: 'CHILD', label: t('ctr.editReasonType.CHILD', 'Child') },
    {
      value: 'DESCRIPTION_FOR_A_PROPER_NAME',
      label: t('ctr.editReasonType.DESCRIPTION_FOR_A_PROPER_NAME', 'Description for a Proper Name'),
    },
    {
      value: 'FIRST_PRESIDENCY_DECISION',
      label: t('ctr.editReasonType.FIRST_PRESIDENCY_DECISION', 'First Presidency Decision'),
    },
    {
      value: 'INFERRED_BY_OTHER_ORDINANCE',
      label: t('ctr.editReasonType.INFERRED_BY_OTHER_ORDINANCE', 'Inferred by Other Ordinance'),
    },
    {
      value: 'LEGACY_SYSTEM_CHANGES_APPLIED',
      label: t('ctr.editReasonType.LEGACY_SYSTEM_CHANGES_APPLIED', 'Legacy System Changes Applied'),
    },
    {
      value: 'LEGACY_SYSTEM_VALIDATION_UNCERTAIN',
      label: t('ctr.editReasonType.LEGACY_SYSTEM_VALIDATION_UNCERTAIN', 'Legacy System Validation Uncertain'),
    },
    { value: 'ORS_MODIFIED_LOR', label: t('ctr.editReasonType.ORS_MODIFIED_LOR', 'ORS Modified LOR') },
    {
      value: 'ORIG_PERSON_TO_CURRENT_OWS_PERSON',
      label: t('ctr.editReasonType.ORIG_PERSON_TO_CURRENT_OWS_PERSON', 'Original Person to Current OWS Person'),
    },
    { value: 'OTHER', label: t('ctr.editReasonType.OTHER', 'Other') },
    {
      value: 'PERFORMED_INCORRECTLY',
      label: t('ctr.editReasonType.PERFORMED_INCORRECTLY', 'Performed Incorrectly'),
    },
    { value: 'PROXY_FOR_LIVING', label: t('ctr.editReasonType.PROXY_FOR_LIVING', 'Proxy for Living') },
    {
      value: 'REPRINTED_ON_NEW_REQUEST',
      label: t('ctr.editReasonType.REPRINTED_ON_NEW_REQUEST', 'Reprinted on new Request'),
    },
    {
      value: 'RERECORD_INVALIDATED_ORDINANCE',
      label: t('ctr.editReasonType.RERECORD_INVALIDATED_ORDINANCE', 'Re-record Invalidated Ordinance'),
    },
    {
      value: 'RECORDED_WRONG_PERFORM_DATE_OR_TEMPLE_CODE',
      label: t(
        'ctr.editReasonType.RECORDED_WRONG_PERFORM_DATE_OR_TEMPLE_CODE',
        'Recorded Wrong Perform Date or Temple Code'
      ),
    },
    {
      value: 'SCANNED_ORDINANCE_NOT_PERFORMED',
      label: t('ctr.editReasonType.SCANNED_ORDINANCE_NOT_PERFORMED', 'Scanned Ordinance not Performed'),
    },
    { value: 'STILLBORN', label: t('ctr.editReasonType.STILLBORN', 'Stillborn') },
    { value: 'UNDER_AGE', label: t('ctr.editReasonType.UNDER_AGE', 'Under Age') },
    {
      value: 'UNWORTHY_PROXY_OR_TEMPLE_WORKER',
      label: t('ctr.editReasonType.UNWORTHY_PROXY_OR_TEMPLE_WORKER', 'Unworthy Proxy or Temple Worker'),
    },
    { value: 'WRONG_GENDER', label: t('ctr.editReasonType.WRONG_GENDER', 'Wrong Gender') },
  ]
  const disableModifyButton = reasonType === 'OTHER' && !reasonText ? true : !reasonType || modifyingCtr
  const modifyCtrBtnOnClick = async () => {
    setModifyingCtr(true)
    await modifyCtr(templePlatform, reasonType, reasonText, modifiedPersons, error, t)
    setModifyingCtr(false)
    overlay.close()
    ctrContext.fetchCurrentCtr()
    ok(t('ctr.summary.createTreePerson.messages.errorEditingCtr', 'Success modifying PIDs in CTR'))
  }

  const cancelBtnOnClick = () => {
    overlay.close()
  }

  return (
    <DialogOverlay
      focusRef={focusElementRef}
      {...overlay}
      title={t('ctr.summary.createTreePerson.modifyCtr.heading', 'Modify Person ID in CTR')}
      closeOnClickAway={false}
    >
      <ModifyCtrPersonTable modifiedPersons={modifiedPersons} />
      <Select
        fullwidth="true"
        label={t('changeType', 'Change Type')}
        value={reasonType}
        onChange={(e) => setReasonType(e.target.value)}
        options={changeCtrReasonOptions}
      />
      {reasonType === 'OTHER' && (
        <TextField
          ref={focusElementRef}
          value={reasonText}
          label={t('reason', 'Reason')}
          placeholder={t('ctr.dialog.editCtrTextFieldPlaceholder', 'Please provide a reason for your edit')}
          onChange={(e) => setReasonText(e.target.value)}
        />
      )}

      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button Icon={ContentEdit} emphasis="high" onClick={modifyCtrBtnOnClick} disabled={disableModifyButton}>
            {t('action.modify', 'Modify')}
          </Button>
          <Button emphasis="medium" onClick={cancelBtnOnClick} disabled={modifyingCtr}>
            {t('action.cancel', 'Cancel')}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}

async function modifyCtr(templePlatform, reasonType, reasonText, modifiedPersons, error, t) {
  try {
    const user = await getUser()
    let contributorId
    await getCisIdAndContributorIdPair(user.cisId, 'cisId').then((response) => {
      contributorId = response.data.contributorId
    })
    const ctrEditRequest = {
      templePlatform: null,
      ctrExtendedDetail: null,
      reasonType,
      reasonText,
      sourceWhoUsysId: `fs-ft.u.${contributorId}`,
      needsAdministrativeReasoning: false,
    }
    modifyPersonIds(templePlatform.persons, modifiedPersons)
    ctrEditRequest.templePlatform = templePlatform
    ctrEditRequest.ctrExtendedDetail = templePlatform.ctrs[0].ctrExtendedDetail[0]
    await putCtr(ctrEditRequest)
  } catch (err) {
    error(
      `${t('ctr.summary.createTreePerson.messages.errorModifyingCtr', 'Error modifying CTR')}: ${err.message || ''}`,
      err
    )
  }
}

function modifyPersonIds(ctrPersons, modifiedPersons) {
  ctrPersons.forEach((ctrPerson) => {
    if (!ctrPerson.id.includes('qr.')) {
      const modifiedPerson = modifiedPersons.find((person) => person.person.id === ctrPerson.id)
      if (modifiedPerson.person.dmc2NewPid) {
        setIdInIdentifiers(
          ctrPerson.identifiers,
          modifiedPerson.person.dmc2NewPid,
          'http://api.familysearch.org/temple/USYS_ID'
        )
      }
    }
  })
}
